import classNames from "classnames";
import cookie from "js-cookie";
import _debounce from "lodash/debounce";
import createActivityDetector from "activity-detector";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KEY_CODE, SORT_DIRECTION } from "lib/constants";
import wsStorage from "lib/wsStorage";

/**
 * Used to do something when someone clicks anywhere in a document EXCEPT
 * for inside of whatever DOM element you assign `ref` to. Generally it's
 * for changing visibility on things like dropdowns (click outside to close
 * a dropdown, but don't do anything if someone clicks inside).
 *
 * @returns [ref, isVisible, setIsVisible]
 */
export const useGlobalClickHandler = ({
    defaultVisible = false,
    onClick = null,
    hideOnClick = true,
} = {}) => {
    const ref = useRef(null);
    const [init, setInit] = useState(!defaultVisible);
    const [isVisible, setIsVisible] = useState(defaultVisible);

    const onGlobalClick = useCallback(
        (event) => {
            if (!ref.current || ref.current.dataset.globalClick === "0") {
                return;
            }

            const shouldHide = hideOnClick || event.target.dataset.closeOnSelect;
            if (
                !shouldHide &&
                (event.target === ref.current || ref.current.contains(event.target))
            ) {
                return;
            }

            document.removeEventListener("click", onGlobalClick, true);

            if (onClick) {
                onClick(event);
            }

            setIsVisible(false);
        },
        [ref],
    );

    const attachGlobalClickHandler = useCallback(() => {
        document.addEventListener("click", onGlobalClick, true);
    }, [ref]);

    const removeGlobalClickHandler = useCallback(() => {
        document.removeEventListener("click", onGlobalClick, true);
    }, [ref]);

    // If isVisible changes, we need to attach or remove the global click handler
    useEffect(() => {
        if (init) {
            if (isVisible) {
                setInit(false);
            }
            return;
        }

        if (isVisible) {
            attachGlobalClickHandler();
        } else {
            removeGlobalClickHandler();
        }
    }, [init, isVisible]);

    return [ref, isVisible, setIsVisible];
};

export const useGlobalEscape = (onEscape, { defaultActive = false } = {}) => {
    const [init, setInit] = useState(!defaultActive);
    const [active, setActive] = useState(defaultActive);

    function onKeyDown(event) {
        if (active && event.keyCode === KEY_CODE.ESCAPE) {
            onEscape();
        }
    }

    const attachHandler = () => {
        document.addEventListener("keydown", onKeyDown, true);
    };

    const removeHandler = () => {
        document.removeEventListener("keydown", onKeyDown, true);
    };

    useEffect(() => {
        if (init) {
            if (active) {
                setInit(false);
            }
            return;
        }

        if (active) {
            attachHandler();
        } else {
            removeHandler();
        }
    }, [init, active]);

    return [active, setActive];
};

/**
 * Creates a local state that gets and syncs its value with localStorage
 *
 * @param key - localStorage key name
 * @param defaultValue - function to return initial value based on what's in storage
 */
export function useStorageState(key, defaultValue = (x) => x) {
    const result = useState(defaultValue(wsStorage.getItem(key)));
    useEffect(() => wsStorage.setItem(key, result[0]), [result[0]]);

    return result;
}

/**
 * Creates a local state that gets and syncs its value with a cookie
 *
 * @param key - cookie key name
 * @param defaultValue - function to return initial value based on what's in the cookie
 */
export function useCookieState(key, defaultValue = (x) => x) {
    const keyName = useMemo(() => `ws${key}`, [key]);
    const result = useState(defaultValue(cookie.get(keyName)));

    useEffect(() => {
        cookie.set(keyName, result[0], { secure: true });
    }, [result[0]]);

    return result;
}

/**
 * Ingests a list and sorts it based on some kinda comparison function. Returns the sorted
 * list and a SortField component that you wrap around whatever element you want to be
 * clickable and do a sort, i.e.:
 *
 * <SortField comparison={(a, b) => a < b ? -1 : 1}><th>Header</th></SortField>
 *
 * When that element is clicked, it'll run the comparison function and automatically
 * determine the sort direction and add a lil arrow icon and all that. Neato!
 *
 * @param list - List to sort
 * @param defaultFunc - Default comparison function to run
 * @param defaultDir - Default sort direction
 */
export function useListSort(list, defaultFunc, defaultDir = SORT_DIRECTION.ASC) {
    const [sortFunc, setSortFunc] = useState(() => defaultFunc);
    const [sortDir, setSortDir] = useState(defaultDir);

    const sorted = useMemo(() => {
        const result = list.slice();

        result.sort(sortFunc);
        if (sortDir === SORT_DIRECTION.DESC) {
            result.reverse();
        }

        return result;
    }, [list, sortFunc, sortDir]);

    function onChangeSort(comparison) {
        let dir = SORT_DIRECTION.ASC;

        if (comparison === sortFunc) {
            dir = sortDir === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;
        }

        setSortFunc(() => comparison);
        setSortDir(dir);
    }

    function SortField({ children, comparison }) {
        let dir = null;
        if (comparison === sortFunc) {
            dir = sortDir;
        }

        return React.cloneElement(children, {
            onClick: () => onChangeSort(comparison),
            className: classNames("ws-sort-field", children.props.className, {
                "ws-sort-field--asc": dir === SORT_DIRECTION.ASC,
                "ws-sort-field--desc": dir === SORT_DIRECTION.DESC,
            }),
        });
    }

    return [sorted, SortField];
}

/**
 * Hook that helps detect when a user becomes idle or active in the page.
 * uses 3rd party module (activity-detector) to listen for any user action
 *
 * @param options - see https://github.com/tuenti/activity-detector for all options object
 * @return boolean
 */
export function useIdle(options) {
    const [isIdle, setIsIdle] = useState(false);
    useEffect(() => {
        const activityDetector = createActivityDetector(options);
        activityDetector.on("idle", () => setIsIdle(true));
        activityDetector.on("active", () => setIsIdle(false));
        return () => activityDetector.stop();
    }, []);
    return isIdle;
}

export function useInterval(callback, delay) {
    // create an interval that will properly access state stuff
    const savedCallback = useRef(callback);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        // Don't schedule if no delay is specified.
        // Note: 0 is a valid value for delay.
        if (!delay && delay !== 0) {
            return null;
        }

        const id = setInterval(() => savedCallback.current(), delay);

        return () => clearInterval(id);
    }, [delay]);
}
